/**
 * Guest-mode kernel — zero dependencies, evaluated first at boot.
 *
 * Resolved synchronously from the pathname so boot-time side effects
 * (Supabase auth restore, analytics, heartbeats) can be suppressed before
 * those modules run. Hard navigation to `/tour` starts a clean module graph.
 *
 * The tour hands off to `/dashboard/*` routes, so the flag also sticks for the
 * tab (sessionStorage): a reload mid-tour stays in the tour instead of
 * bouncing to sign-in. Booting on any other path clears it.
 */

const STICKY_KEY = 'lh_guest_tour'

function pathIsGuestTour(pathname: string): boolean {
    const path = pathname.replace(/\/+$/, '') || '/'
    return path === '/tour' || path.startsWith('/tour/')
}

function resolveGuestMode(): boolean {
    if (typeof window === 'undefined') return false
    const path = window.location.pathname
    try {
        if (pathIsGuestTour(path)) {
            sessionStorage.setItem(STICKY_KEY, '1')
            return true
        }
        if (path.startsWith('/dashboard') && sessionStorage.getItem(STICKY_KEY) === '1') return true
        sessionStorage.removeItem(STICKY_KEY)
    } catch {
        return pathIsGuestTour(path)
    }
    return false
}

export const GUEST_MODE = resolveGuestMode()

export function isGuestMode(): boolean {
    return GUEST_MODE
}

/** Leaving the tour on purpose (sign up, home): the next load is a normal one. */
export function clearGuestTour(): void {
    try {
        sessionStorage.removeItem(STICKY_KEY)
    } catch {
        // Nothing to clear.
    }
}

/** Dev-only: blocked egress throws instead of returning inert payloads. */
export function isGuestStrictMode(): boolean {
    return (
        GUEST_MODE &&
        (import.meta.env.VITE_APP_GUEST_STRICT ?? '') === 'true'
    )
}
